import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, X } from "lucide-react";
import { API } from "../lib/api";

const QUICK = [
  "What sizes should I pick?",
  "Recommend a gift under £20",
  "How does delivery work?",
  "What's your returns policy?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I'm Sparky! Ask me anything — sizes, delivery, returns — or say 'recommend a gift' and I'll point you to something lovely from our collection.",
      picks: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    const history = messages.slice(-6).map((m) => ({ role: m.role, text: m.text }));
    setMessages((m) => [...m, { role: "user", text: msg, picks: [] }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: msg, history });
      setMessages((m) => [
        ...m,
        { role: "assistant", text: res.data.reply, picks: res.data.picks || [] },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            err?.response?.data?.detail ||
            "Sorry — I'm having a little moment. Please try again.",
          picks: [],
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3" data-testid="chat-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="flex h-[480px] max-h-[70vh] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-[28px] border border-lav-100 bg-white shadow-2xl shadow-lav-100"
          >
            <div className="flex items-center justify-between border-b border-lav-100 bg-gradient-to-r from-blush-50 to-lav-50 px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm">
                  <Sparkles className="h-4 w-4 text-blush-600" />
                  <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-mint-500" />
                </span>
                <div>
                  <p className="font-display text-base font-semibold leading-none text-ink">Sparky</p>
                  <p className="mt-1 text-[11px] text-muted">Here to help, always</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="chat-close-button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted transition-colors duration-200 hover:bg-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "ml-auto bg-ink text-white"
                        : "bg-blush-50 text-ink/85"
                    }`}
                  >
                    {m.text}
                  </div>
                  {m.picks?.length > 0 && (
                    <div className="mt-2 space-y-2" data-testid={`chat-picks-${i}`}>
                      {m.picks.map((p) => (
                        <Link
                          key={p.id}
                          to={`/shop?cat=${p.category_id}`}
                          data-testid={`chat-pick-${p.id}`}
                          className="flex items-center gap-3 rounded-2xl border border-lav-100 bg-white p-2.5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                        >
                          <img src={p.image} alt={p.name} className="h-12 w-10 shrink-0 rounded-xl object-cover" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-ink">{p.name}</p>
                            <p className="text-xs font-bold text-blush-700">£{p.price.toFixed(2)}</p>
                          </div>
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
                            View
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex w-16 items-center justify-center gap-1 rounded-2xl bg-blush-50 px-4 py-3" data-testid="chat-typing">
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-blush-500"
                      style={{ animationDelay: `${d * 0.15}s` }}
                    />
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            {messages.length <= 2 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid={`chat-quick-${q.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                    className="rounded-full border border-lav-100 bg-cream px-3.5 py-2 text-xs font-semibold text-muted transition-colors duration-200 hover:border-blush-300 hover:text-ink"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-lav-100 p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about sizes, delivery..."
                data-testid="chat-input"
                className="w-full rounded-full border border-lav-100 bg-cream px-4 py-2.5 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-muted/60 focus:border-blush-500"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                aria-label="Send message"
                data-testid="chat-send-button"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-white transition-transform duration-300 hover:scale-105 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Chat with Sparky"}
        data-testid="chat-bubble-button"
        className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-ink shadow-xl shadow-ink/25"
      >
        {open ? <X className="h-6 w-6 text-white" /> : <Sparkles className="h-6 w-6 text-blush-300" />}
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blush-500 opacity-75" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-blush-600" />
          </span>
        )}
      </motion.button>
    </div>
  );
}
